import { IPublicClientApplication, InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { SCOPES } from "./config";
import { msalInstance } from "../App";

const storeAccessToken = (token: string) => {
  sessionStorage.setItem("accessToken", token);
};

const getAccessTokenFromStorage = () => {
  return sessionStorage.getItem("accessToken");
};

// Use a Map to track individual token acquisition requests
const tokenAcquisitionPromises = new Map<string, Promise<string | null>>();

const isTokenExpired = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])); // Decode JWT
    const exp = payload.exp; // Expiration time
    const expired = Date.now() >= exp * 1000;
    return expired;
  } catch (error) {
    // If we can't parse the token, consider it expired
    return true;
  }
};

export async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    console.log("No accounts found, user needs to login");
    return null;
  }

  const account = accounts[0];
  const scope = SCOPES;
  const cacheKey = `${account.homeAccountId}-${scope}`;

  // Check if we already have a pending request for this account/scope
  if (tokenAcquisitionPromises.has(cacheKey)) {
    return tokenAcquisitionPromises.get(cacheKey)!;
  }

  // Check for valid stored token first
  const storedToken = getAccessTokenFromStorage();
  if (storedToken && !isTokenExpired(storedToken)) {
    return storedToken;
  }

  // Create a new token acquisition promise
  const tokenPromise = acquireTokenWithFallback(account, scope);
  tokenAcquisitionPromises.set(cacheKey, tokenPromise);

  try {
    const result = await tokenPromise;
    return result;
  } finally {
    // Clean up the promise from the map
    tokenAcquisitionPromises.delete(cacheKey);
  }
}

async function acquireTokenWithFallback(account: any, scope: string): Promise<string | null> {
  try {
    // First try silent token acquisition
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    
    storeAccessToken(response.accessToken);
    return response.accessToken;
  } catch (error) {
    console.log("Silent token acquisition failed:", error);
    
    // Check if it's an interaction required error or conditional access error
    if (error instanceof InteractionRequiredAuthError || 
        (error as any)?.errorCode === "interaction_required" ||
        (error as any)?.errorCode === "consent_required" ||
        (error as any)?.errorCode === "login_required" ||
        (error as any)?.errorMessage?.includes("AADSTS53003") ||
        (error as any)?.errorMessage?.includes("interaction_required")) {
      
      console.log("Interactive authentication required, falling back to popup");
      
      try {
        // Fall back to popup authentication
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
          account: account,
        });
        
        storeAccessToken(response.accessToken);
        return response.accessToken;
      } catch (popupError) {
        console.error("Popup authentication failed:", popupError);
        
        // If popup fails, try redirect as last resort
        if (popupError instanceof BrowserAuthError && 
            (popupError as any).errorCode === "popup_window_error") {
          console.log("Popup blocked, attempting redirect authentication");
          
          try {
            const response = await msalInstance.acquireTokenRedirect({
              scopes: [scope],
              account: account,
            });
            
            // For redirect, we need to wait for the redirect to complete
            // This will be handled by the app's redirect handling logic
            return null;
          } catch (redirectError) {
            console.error("Redirect authentication failed:", redirectError);
            return null;
          }
        }
        
        return null;
      }
    } else {
      console.error("Unexpected authentication error:", error);
      return null;
    }
  }
}

// Logout method
export const logout = async (instance: IPublicClientApplication) => {
  // Clear stored tokens
  sessionStorage.removeItem("accessToken");
  
  // Clear all token acquisition promises
  tokenAcquisitionPromises.clear();
  
  await instance.logoutRedirect({
    postLogoutRedirectUri: "/", // Redirect to this URL after logout
  });
};

// Helper function to check if user is logged in
export const isUserLoggedIn = (): boolean => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0;
};

// Helper function to get current account
export const getCurrentAccount = () => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};
